/**
 * @description React context for tenant branding — wraps entire app
 *
 * Key features:
 * - Resolves tenant branding from domain via useTenantBranding hook
 * - Injects brand CSS custom properties on load
 * - Updates favicon / apple-touch-icon / og:image dynamically (progressive;
 *   Worker HTMLRewriter is SoT on SO hosts). document.title stays in layouts.
 * - Dark/light mode management with system preference detection + user toggle
 * - Exposes branding config and mode controls to all components
 *
 * @dependencies
 * - useTenantBranding: Domain detection + branding fetch
 * - colorUtils: CSS variable injection
 * - brandingDefaults: LynxPay (platform) vs Lynx/generic (CP/SO) overlays
 * - webIdentity: progressive favicon / apple-touch-icon / og:image apply
 *
 * @notes
 * - BrandingProvider should wrap the app above AuthProvider in component tree
 * - Dark mode preference stored in localStorage (key: 'lynx_dark_mode')
 * - System preference detected via matchMedia('prefers-color-scheme: dark')
 * - On load: injects CSS vars → swaps favicon → applies dark/light class
 * - FOUC prevention: index.css provides default CSS var values synchronously;
 *   BrandingContext overrides them once branding data loads
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import useTenantBranding from '../hooks/useTenantBranding';
import { generateBrandVariants, injectBrandCSS } from '../utils/colorUtils';
import { applyDocumentWebIdentity, resolveWebIdentity } from '../utils/webIdentity';

const BrandingContext = createContext(null);

const DARK_MODE_KEY = 'lynx_dark_mode';

/**
 * Detect system color scheme preference
 * @returns {boolean} true if system prefers dark mode
 */
function getSystemPreference() {
  if (typeof window === 'undefined') return true;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
}

/**
 * Get stored dark mode preference (localStorage) or fall back to system/branding default
 * @param {string} brandingMode - 'dark' or 'light' from branding config
 * @returns {boolean} true for dark mode
 */
function getInitialDarkMode(brandingMode) {
  const stored = localStorage.getItem(DARK_MODE_KEY);
  if (stored !== null) {
    return stored === 'true';
  }
  // Fall back to branding config mode, then system preference
  if (brandingMode === 'light') return false;
  if (brandingMode === 'dark') return true;
  return getSystemPreference();
}

export function BrandingProvider({ children }) {
  const { branding, isLoading, isDefault, slug, signupEnabled, occupancy, organizationName } = useTenantBranding();
  const [darkMode, setDarkMode] = useState(() => getInitialDarkMode('dark'));

  // Inject CSS custom properties when branding loads
  useEffect(() => {
    if (isLoading) return;

    const { primaryColor, secondaryColor } = branding.colors;
    const variants = generateBrandVariants(primaryColor, secondaryColor);
    injectBrandCSS(variants);
  }, [isLoading, branding.colors]);

  // Update dark mode from branding config (only when no user preference is stored)
  useEffect(() => {
    if (isLoading) return;
    const stored = localStorage.getItem(DARK_MODE_KEY);
    if (stored === null) {
      setDarkMode(branding.colors.mode === 'dark');
    }
  }, [isLoading, branding.colors.mode]);

  // Apply dark/light class to <html> element
  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [darkMode]);

  // document.title is now managed per-page by layout components
  // (BaseSidebarLayout, AuthLayout) using usePageTitle hook or inline effects.
  // Format: "PageTitle | PlatformName" — see frontend/src/hooks/usePageTitle.js

  // Update meta description
  useEffect(() => {
    if (isLoading) return;
    const desc = branding.meta.description;
    if (!desc) return;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = desc;
  }, [isLoading, branding.meta.description]);

  // Progressive web-identity. Depend on primitive fields (not the branding
  // object) so exhaustive-deps is honest and overlay identity changes do not
  // no-op. Empty CP/SO URLs stay null — never LynxPay.
  const faviconUrl = branding.assets?.faviconUrl;
  const logoMarkUrl = branding.assets?.logoMarkUrl;
  const logoUrl = branding.assets?.logoUrl;
  const platformName = branding.identity?.platformName;
  const identityThemeColor = branding.meta?.themeColor;
  const identityDescription = branding.meta?.description;
  const primaryColor = branding.colors?.primaryColor;

  useEffect(() => {
    if (isLoading) return;
    applyDocumentWebIdentity(resolveWebIdentity({
      assets: { faviconUrl, logoMarkUrl, logoUrl },
      identity: { platformName },
      meta: { themeColor: identityThemeColor, description: identityDescription },
      colors: { primaryColor }
    }));
  }, [isLoading, faviconUrl, logoMarkUrl, logoUrl, platformName, identityThemeColor, identityDescription, primaryColor]);

  // Update theme-color meta tag
  useEffect(() => {
    if (isLoading) return;

    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = branding.meta.themeColor;
  }, [isLoading, branding.meta.themeColor]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      // Only follow system if user hasn't set a preference
      const stored = localStorage.getItem(DARK_MODE_KEY);
      if (stored === null) {
        setDarkMode(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem(DARK_MODE_KEY, String(next));
      return next;
    });
  }, []);

  const value = {
    branding,
    isLoading,
    isDefault,
    slug,
    signupEnabled,
    occupancy,
    organizationName,
    darkMode,
    toggleDarkMode
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * Hook to access branding context
 * @returns {{ branding, isLoading, isDefault, slug, signupEnabled, occupancy, organizationName, darkMode, toggleDarkMode }}
 */
export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

export default BrandingContext;
